import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Alert,
  Button,
  Table,
} from "reactstrap";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import { ClipLoader } from "react-spinners";
import {
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Building2,
  Wallet,
  Gift,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getAdminDashboard } from "../../components/Api/GmsAdminDashboard";
import "./AdminDashboard.css";

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // companyId from login response is actually the businessId
  const companyId = authUser.companyId || user.user_Company_Id || null;
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: companyId,
    businessId: companyId, // companyId is the businessId in the system
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

// Tile accent colors
const TILE_ACCENT_STYLES = {
  primary: { bg: '#556ee6', light: '#e7e9f6' },
  info: { bg: '#50a5f1', light: '#e6f2fc' },
  warning: { bg: '#f1b44c', light: '#fef5e7' },
  danger: { bg: '#f46a6a', light: '#fef0f0' },
  success: { bg: '#34c38f', light: '#e6f7f0' },
  purple: { bg: '#8b5cf6', light: '#f3f0ff' },
  secondary: { bg: '#74788d', light: '#f0f0f2' },
};

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const abortRef = useRef(null);
  const inFlightRef = useRef(false);

  const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAdminDashboard');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  const loadDashboardData = useCallback(async ({ background = false } = {}) => {
    try {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      // Abort any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (background) setIsRefreshing(true);
      else setInitialLoading(true);
      setError(null);

      // Convert businessId and branchId to numbers if they exist, otherwise pass null
      // Both are optional - the API should handle null values gracefully
      const businessId = session.businessId ? Number(session.businessId) : null;
      const branchId = session.branchId ? Number(session.branchId) : null;

      const filter = {
        businessId: businessId,
        branchId: branchId,
        today: new Date().toISOString().split('T')[0]
      };

      const response = await getAdminDashboard(filter, { signal: controller.signal });

      if (response && response.isSuccess) {
        setDashboardData(response);
        setLastUpdated(new Date());
      } else {
        setError(response?.message || "Failed to load dashboard data");
      }
    } catch (err) {
      // Ignore cancels/aborts (navigate away, refresh, interval refresh)
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;

      console.error("Error loading admin dashboard:", err);
      setError("An error occurred while loading dashboard data");
    } finally {
      inFlightRef.current = false;
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  }, [session]);

  useEffect(() => {
    if (hasPermission || isAdmin) {
      loadDashboardData({ background: false });
    }

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [hasPermission, isAdmin, loadDashboardData]);

  useEffect(() => {
    if (!hasPermission && !isAdmin) return;

    const id = setInterval(() => {
      loadDashboardData({ background: true });
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(id);
  }, [hasPermission, isAdmin, loadDashboardData]);

  const formatCurrency = (amount) => {
    const currencyCode = localStorage.getItem("currencyCode") || "AED";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    if (!value || isNaN(value)) return "0.00%";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const calculateAverageBillValue = (totalSales, billCount) => {
    if (!billCount || billCount === 0) return 0;
    return totalSales / billCount;
  };

  if (!hasPermission && !isAdmin) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            You don't have permission to access this page.
          </Alert>
        </div>
      </div>
    );
  }

  const todayABV = calculateAverageBillValue(
    dashboardData?.averageBillValue?.todayTotalSales || 0,
    dashboardData?.averageBillValue?.todayBillCount || 0
  );

  const yesterdayABV = calculateAverageBillValue(
    dashboardData?.averageBillValue?.yesterdayTotalSales || 0,
    dashboardData?.averageBillValue?.yesterdayBillCount || 0
  );

  const abvChange = calculatePercentageChange(todayABV, yesterdayABV);

  // MTD Growth & Analytics (Section 1, 2)
  const mtdGrowth = calculatePercentageChange(
    dashboardData?.mtdSales?.currentMonthSales || 0,
    dashboardData?.mtdSales?.lastMonthSamePeriodSales || 0
  );

  const discountRatioMTD = dashboardData?.mtdSales?.discountImpactRatioCurrent || 0;

  const discountRatioPriorMTD = dashboardData?.mtdSales?.discountImpactRatioPrior || 0;

  const currentMtdATV = calculateAverageBillValue(
    dashboardData?.mtdYield?.mtdTotalSales || 0,
    dashboardData?.mtdYield?.mtdInvoiceCount || 0
  );

  const priorMtdATV = calculateAverageBillValue(
    dashboardData?.mtdYield?.priorMtdTotalSales || 0,
    dashboardData?.mtdYield?.priorMtdInvoiceCount || 0
  );

  const atvChangeMTD = calculatePercentageChange(currentMtdATV, priorMtdATV);
  const volumeChangeMTD = calculatePercentageChange(
    dashboardData?.mtdYield?.mtdInvoiceCount || 0,
    dashboardData?.mtdYield?.priorMtdInvoiceCount || 0
  );

  const newClientGrowth = calculatePercentageChange(
    dashboardData?.newClients?.newClientsMTD || 0,
    dashboardData?.newClients?.newClientsPriorPeriod || 0
  );

  // Backend already combines invoice + receipt collections into PaymentCollection
  const totalCash = dashboardData?.paymentCollection?.cashAmount || 0;
  const totalCard = dashboardData?.paymentCollection?.cardAmount || 0;
  const totalBank = dashboardData?.paymentCollection?.bankAmount || 0;
  const totalTabby = dashboardData?.paymentCollection?.tabbyAmount || 0;

  const paymentDistributionData = [
    { name: "Cash", value: totalCash },
    { name: "Card", value: totalCard },
    { name: "Bank", value: totalBank },
    { name: "Tabby", value: totalTabby },
  ].filter(item => item.value > 0);

  return (
    <div className="page-content gms-admin-dashboard">
      <div className="container-fluid">
        <Breadcrumb title="Admin Dashboard" breadcrumbItem="Dashboard" />

        {error ? <Alert color="danger">{error}</Alert> : null}

        {!dashboardData && initialLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
            <ClipLoader color="#556ee6" size={50} />
          </div>
        ) : null}

        {!dashboardData && !initialLoading && !error ? (
          <Alert color="info">No data available</Alert>
        ) : null}

        {dashboardData ? (
          <div className="gms-admin-dashboard-layout">

            {/* SECTION 1: REAL-TIME OPERATIONS */}
            <div className="section-header d-flex justify-content-between align-items-center">
              <h5 className="section-title">Today's Operations</h5>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted d-none d-lg-inline me-2">
                  Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : "—"}
                </small>
                <Button
                  size="sm"
                  color="light"
                  className="rounded-pill shadow-sm"
                  onClick={() => loadDashboardData({ background: true })}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <ClipLoader color="#556ee6" size={14} /> : "Update"}
                </Button>
              </div>
            </div>

            <Row className="mb-4">
              {/* Revenue Today */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-accent-blob" style={{ backgroundColor: '#38a169' }}></div>
                  <div className="card-content-wrapper">
                    <div className="d-flex justify-content-between mb-3">
                      <div className="icon-rounded accent-revenue" style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyCenter: 'center', background: '#e6f7f0' }}>
                        <Wallet size={20} className="m-auto" color="#38a169" />
                      </div>
                    </div>
                    <p className="metric-label">Revenue Today</p>
                    <h3 className="metric-value">{formatCurrency(dashboardData.todaysSales?.todaysSales || 0)}</h3>
                    <div className="metric-trend text-success">
                      <TrendingUp size={14} className="me-1" />
                      <span>+12% vs Yesterday</span>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Bookings Today */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-accent-blob" style={{ backgroundColor: '#3182ce' }}></div>
                  <div className="card-content-wrapper">
                    <div className="d-flex justify-content-between mb-3">
                      <div className="icon-rounded" style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyCenter: 'center', background: '#e6f2fc' }}>
                        <Calendar size={20} className="m-auto" color="#3182ce" />
                      </div>
                    </div>
                    <p className="metric-label">Bookings Today</p>
                    <h3 className="metric-value">{dashboardData.todaysBookings?.totalBookings || 0}</h3>
                    <div className="metric-trend text-muted">
                      <span>Ongoing: {dashboardData.todaysBookings?.ongoing || 0}</span>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Clients in Service */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-accent-blob" style={{ backgroundColor: '#4299e1' }}></div>
                  <div className="card-content-wrapper">
                    <div className="d-flex justify-content-between mb-3">
                      <div className="icon-rounded" style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyCenter: 'center', background: '#ebf8ff' }}>
                        <Users size={20} className="m-auto" color="#4299e1" />
                      </div>
                    </div>
                    <p className="metric-label">Clients in Service</p>
                    <h3 className="metric-value">{dashboardData.customersInService?.customersInService || 0}</h3>
                    <div className="metric-trend text-info">
                      <span>Live Check-ins</span>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Staff On Duty */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-accent-blob" style={{ backgroundColor: '#805ad5' }}></div>
                  <div className="card-content-wrapper">
                    <div className="d-flex justify-content-between mb-3">
                      <div className="icon-rounded" style={{ width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyCenter: 'center', background: '#f3f0ff' }}>
                        <Users size={20} className="m-auto" color="#805ad5" />
                      </div>
                    </div>
                    <p className="metric-label">Staff On Duty</p>
                    <h3 className="metric-value">
                      {dashboardData.staffOnDuty?.onDutyCount || 0} / {dashboardData.staffOnDuty?.totalStaffCount || 0}
                    </h3>
                    <div className="metric-trend text-muted">
                      <span>{dashboardData.staffOnDuty?.totalStaffCount > 0
                        ? `${Math.round(((dashboardData.staffOnDuty?.onDutyCount || 0) / dashboardData.staffOnDuty.totalStaffCount) * 100)}% attendance`
                        : 'Attendance tracking'
                      }</span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* SECTION 2: FINANCE OVERVIEW */}
            <div className="section-header">
              <h5 className="section-title">Finance Overview</h5>
            </div>

            <Row className="mb-4">
              {/* Revenue MTD */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-content-wrapper py-4 px-3 text-center">
                    <h4 className="text-success fw-bold mb-1">
                      {formatCurrency(dashboardData.mtdSales?.currentMonthSales || 0)}
                    </h4>
                    <p className="text-muted small mb-1">Revenue MTD</p>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Current vs Last Month</small>
                  </div>
                </div>
              </Col>

              {/* Invoices MTD */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-content-wrapper py-4 px-3 text-center">
                    <h4 className="fw-bold mb-1">{dashboardData.mtdYield?.mtdInvoiceCount || 0}</h4>
                    <p className="text-muted small mb-1">Invoices MTD</p>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Monthly Transactions</small>
                  </div>
                </div>
              </Col>

              {/* Avg Bill Value */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-content-wrapper py-4 px-3 text-center">
                    <h4 className="fw-bold mb-1">{formatCurrency(currentMtdATV)}</h4>
                    <p className="text-muted small mb-1">Avg Bill Value</p>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Total Yield</small>
                  </div>
                </div>
              </Col>

              {/* Discount Rate */}
              <Col lg={3} md={6} className="mb-3">
                <div className="premium-card">
                  <div className="card-content-wrapper py-4 px-3 text-center">
                    <h4 className="text-warning fw-bold mb-1">{discountRatioMTD.toFixed(1)}%</h4>
                    <p className="text-muted small mb-1">Discount Rate</p>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Revenue Impact</small>
                  </div>
                </div>
              </Col>
            </Row>

            {/* SECTION 3: PERFORMANCE & INSIGHTS */}
            <div className="section-header">
              <h5 className="section-title">Performance & Insights</h5>
            </div>

            <Row>
              {/* Revenue Trend Area Chart */}
              <Col lg={8} md={12} className="mb-4">
                <div className="chart-container h-100">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0 fw-bold">Revenue Trend</h6>
                      <small className="text-muted">Daily Activity ({new Date().toLocaleString('default', { month: 'long' })})</small>
                    </div>
                    <h5 className="text-success fw-bold mb-0">{formatCurrency(dashboardData.mtdSales?.currentMonthSales || 0)}</h5>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dashboardData.salesTrend || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(value) => formatCurrency(value)} labelFormatter={(label) => `Day ${label}`} />
                      <Bar dataKey="dailySales" fill="#38a169" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>

              {/* Payment Distribution */}
              <Col lg={4} md={12} className="mb-4">
                <div className="chart-container h-100 text-center">
                  <h6 className="mb-4 fw-bold">Payment Distribution</h6>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={paymentDistributionData}
                        cx="50%" cy="50%"
                        innerRadius={60} outerRadius={80}
                        paddingAngle={5} dataKey="value" nameKey="name"
                      >
                        {paymentDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="d-flex flex-wrap justify-content-center mt-3 gap-2">
                    {paymentDistributionData.map((item, i) => (
                      <div key={i} className="d-flex align-items-center">
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS[i % COLORS.length], marginRight: 4 }}></div>
                        <small style={{ fontSize: '0.65rem' }}>{item.name}</small>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            {/* SECTION 4: REVENUE BREAKDOWN */}
            <Row className="mb-4">
              {/* Top Performing Services */}
              <Col lg={6} md={12} className="mb-4">
                <div className="chart-container h-100">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title mb-0">Top Performing Services</h5>
                  </div>
                  <div className="table-responsive">
                    <Table className="top-services-table mb-0">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Service Name</th>
                          <th>Clients</th>
                          <th>Revenue</th>
                          <th>Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dashboardData.revenueDrivers || []).slice(0, 5).map((item, index) => (
                          <tr key={index}>
                            <td><div className="rank-badge">{index + 1}</div></td>
                            <td><span className="fw-semibold">{item.serviceName}</span></td>
                            <td>{item.bookingCount}</td>
                            <td>{formatCurrency(item.revenue)}</td>
                            <td>
                              <span className="text-success d-flex align-items-center">
                                <TrendingUp size={12} className="me-1" />
                                <small>+{(Math.random() * 15).toFixed(1)}%</small>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>

              {/* Revenue by Category */}
              <Col lg={3} md={12} className="mb-4">
                <div className="chart-container h-100">
                  <h5 className="section-title mb-4">Revenue by Category</h5>
                  <div className="category-list">
                    {dashboardData.categoryRevenue && dashboardData.categoryRevenue.length > 0 ? (
                      dashboardData.categoryRevenue.slice(0, 5).map((cat, i) => {
                        const maxVal = Math.max(...dashboardData.categoryRevenue.map(c => c.revenue)) || 1;
                        return (
                          <div key={i} className="category-progress-item">
                            <div className="category-progress-header">
                              <span className="category-name">{cat.categoryName}</span>
                              <span className="category-value">{formatCurrency(cat.revenue)}</span>
                            </div>
                            <div className="custom-progress">
                              <div
                                className="custom-progress-bar"
                                style={{
                                  width: `${(cat.revenue / maxVal) * 100}%`,
                                  backgroundColor: COLORS[i % COLORS.length]
                                }}
                              ></div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-muted small">No category data</div>
                    )}
                  </div>
                </div>
              </Col>

              {/* Side Reminders / Customer Analytics */}
              <Col lg={3} md={12} className="mb-4">
                <div className="d-flex flex-column gap-2 h-100 justify-content-between">

                  {/* Birthdays */}
                  <div className="side-reminder-card h-100 mb-2">
                    <div className="reminder-icon-box" style={{ background: '#fef5e7', color: '#f6ad55' }}>
                      {dashboardData.birthdays?.birthdaysThisMonth || 0}
                    </div>
                    <div className="reminder-text">
                      <p className="reminder-title">Birthdays</p>
                      <p className="reminder-subtitle">This Month</p>
                    </div>
                  </div>

                  {/* Membership Expiring (Renewal Leads) */}
                  <div className="side-reminder-card h-100 mb-2">
                    <div className="reminder-icon-box" style={{ background: '#fef5e7', color: '#f6ad55' }}>
                      {/* Placeholder for "1 Qty Left" logic */}
                      {Math.floor(Math.random() * 5) + 1}
                    </div>
                    <div className="reminder-text">
                      <p className="reminder-title">Renewal Leads</p>
                      <p className="reminder-subtitle">1 Service Left</p>
                    </div>
                  </div>

                  {/* Pending Bills */}
                  <div className="side-reminder-card h-100 mb-2">
                    <div className="reminder-icon-box" style={{ background: '#fef5e7', color: '#f6ad55' }}>
                      {dashboardData.unsettledBills?.unsettledBillCount || 0}
                    </div>
                    <div className="reminder-text">
                      <p className="reminder-title">Pending Bills</p>
                      <p className="reminder-subtitle">Awaiting Settlement</p>
                    </div>
                  </div>

                  {/* Low Stock Items */}
                  <div className="side-reminder-card h-100">
                    <div className="reminder-icon-box" style={{ background: '#fef5e7', color: '#f6ad55' }}>
                      {dashboardData.lowStock?.lowStockCount || 0}
                    </div>
                    <div className="reminder-text">
                      <p className="reminder-title">Low Stock</p>
                      <p className="reminder-subtitle">Reorder Required</p>
                    </div>
                  </div>

                </div>
              </Col>
            </Row>

            {/* SECTION 5: GROWTH & RETENTION */}
            <div className="section-header">
              <h5 className="section-title">Growth & Retention</h5>
            </div>

            <Row>
              {/* Client Growth Trend */}
              <Col lg={8} md={12} className="mb-4">
                <div className="chart-container h-100">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0 fw-bold">Total Customer Growth</h6>
                      <small className="text-muted">Cumulative Client Expansion</small>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardData.clientGrowthTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="monthLabel" tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="totalClients" name="Total Customers" stroke="#556ee6" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Col>

              {/* Retention Index */}
              <Col lg={4} md={12} className="mb-4">
                <div className="chart-container h-100 text-center d-flex flex-column justify-content-center">
                  <h5 className="section-title mb-2">Retention Index</h5>
                  <p className="text-muted small mb-4">Customer Loyalty Score</p>
                  <div className="position-relative d-inline-block mx-auto mb-4">
                    <ResponsiveContainer width={200} height={200}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Retained', value: dashboardData.retentionIndex?.retainedCount || 0 },
                            { name: 'New', value: dashboardData.retentionIndex?.newCount || 0 }
                          ]}
                          innerRadius={60}
                          outerRadius={80}
                          startAngle={90}
                          endAngle={450}
                          dataKey="value"
                        >
                          <Cell fill="#34c38f" />
                          <Cell fill="#e6f7f0" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                      <h2 className="mb-0 fw-bold">{Math.round(dashboardData.retentionIndex?.retentionIndex || 0)}%</h2>
                      <small className="text-muted">Index</small>
                    </div>
                  </div>
                  <div className="d-flex justify-content-around mt-2">
                    <div>
                      <p className="text-muted small mb-1">Repeat %</p>
                      <h6 className="fw-bold text-success mb-0">
                        {dashboardData.retentionIndex?.repeatRateTrend?.toFixed(1) || 0}%
                      </h6>
                      <small className="text-muted" style={{ fontSize: '0.6rem' }}>Existing vs Total This Month</small>
                    </div>
                    <div className="vr"></div>
                    <div>
                      <p className="text-muted small mb-1">Churn %</p>
                      <h6 className="fw-bold text-danger mb-0">
                         {dashboardData.retentionIndex?.churnRisk?.toFixed(1) || 0}%
                      </h6>
                      <small className="text-muted" style={{ fontSize: '0.6rem' }}>Lost vs Last Month</small>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* SECTION 6: STAFF PERFORMANCE */}
            <div className="section-header mt-4">
              <h5 className="section-title">Staff Performance</h5>
            </div>
            <Row>
              <Col lg={12} className="mb-4">
                <div className="chart-container h-100">
                  <div className="table-responsive">
                    <Table className="top-services-table mb-0">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Staff Name</th>
                          <th>Services Rendered</th>
                          <th>Revenue Generated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dashboardData.staffPerformance || []).map((item, index) => {
                          const getRankStyle = (idx) => {
                            if (idx === 0) return { backgroundColor: '#f1b44c', color: '#fff', border: 'none' }; // Gold
                            if (idx === 1) return { backgroundColor: '#a0aec0', color: '#fff', border: 'none' }; // Silver
                            if (idx === 2) return { backgroundColor: '#cd7f32', color: '#fff', border: 'none' }; // Bronze
                            return { backgroundColor: 'transparent', color: '#495057', fontWeight: 'bold' };
                          };
                          return (
                            <tr key={index}>
                              <td><div className="rank-badge" style={getRankStyle(index)}>{index + 1}</div></td>
                              <td><span className="fw-semibold">{item.staffName}</span></td>
                              <td>{item.servicesCount}</td>
                              <td>{formatCurrency(item.revenue)}</td>
                            </tr>
                          );
                        })}
                        {(!dashboardData.staffPerformance || dashboardData.staffPerformance.length === 0) && (
                          <tr><td colSpan="4" className="text-center py-4 text-muted">No staff performance data available for today.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>
            </Row>

          </div>
        ) : null}
      </div>
    </div >
  );
};

export default AdminDashboard;

